import { useEffect, useState } from "react";

import {
  formatContagem,
  formatDataCurta,
  formatDuracao,
  formatFracao,
  formatPercentual,
  formatQuantidade,
  formatValorCompacto,
} from "../../home.format";
import type { ObrasViewProps, ObrasViewViewModel } from "./types-obras-view";

/** Legenda do valor no Financeiro do mobile (Figma 7260:67556); o desktop mostra a do dado ("Valor Total"). */
const LEGENDA_MOBILE = "Total – BRL";

const GMUD_NATUREZAS = [
  { key: "adicao", label: "Adição", icon: "pi pi-file-plus" },
  { key: "exclusao", label: "Exclusão", icon: "pi pi-file-excel" },
  { key: "alteracao", label: "Alteração", icon: "pi pi-file-edit" },
  { key: "quebra", label: "Quebra", icon: "pi pi-clock" },
] as const;

/** ViewModel das Obras em Andamento: a Obra em destaque, já formatada, e o carrossel entre elas. */
export function useObrasView({ obrasEmAndamento, isNarrow }: ObrasViewProps): ObrasViewViewModel {
  const { obras } = obrasEmAndamento;
  const [index, setIndex] = useState(0);

  // Uma consulta nova pode trazer menos Obras do que a anterior; sem isto o
  // destaque ficaria em um índice que não existe mais.
  useEffect(() => {
    setIndex((current) => (current < obras.length ? current : 0));
  }, [obras.length]);

  const total = obras.length;
  const irPara = (proximo: number) => setIndex((proximo + total) % total);
  const atual = obras[index];

  return {
    obra: atual
      ? {
          id: atual.id,
          testId: `home-obra-${atual.id}`,
          chamada: atual.chamada,
          descricao: atual.descricao,
          inicio: formatDataCurta(atual.periodo.inicio),
          termino: formatDataCurta(atual.periodo.termino),
          duracao: formatDuracao(atual.periodo.duracaoDias),
          financeiro: {
            valor: formatValorCompacto(atual.financeiro.valorTotal),
            fel: atual.financeiro.fel,
            legenda: isNarrow ? LEGENDA_MOBILE : atual.financeiro.legenda,
            serie: atual.financeiro.serie,
            indiceHoje: atual.financeiro.indiceHoje,
            formatValue: formatValorCompacto,
            fases: atual.financeiro.fases.map((fase) => ({
              codigo: fase.codigo,
              sigla: fase.sigla,
              testId: `home-fase-${fase.sigla}`,
              percentual: formatPercentual(fase.percentual),
            })),
          },
          progresso: atual.progresso.map((item) => ({
            label: item.label,
            percentual: formatPercentual(item.percentual),
            fracao: formatFracao(item.realizado, item.total),
          })),
          gmud: {
            aprovadas: formatQuantidade(atual.gmud.aprovadas),
            total: `/${formatQuantidade(atual.gmud.total)}`,
            naturezas: GMUD_NATUREZAS.map((natureza) => ({
              ...natureza,
              valor: formatContagem(atual.gmud[natureza.key]),
              testId: `home-gmud-${natureza.key}`,
            })),
          },
        }
      : null,
    temVarias: total > 1,
    indicadores: obras.map((item, itemIndex) => ({
      id: item.id,
      ativo: itemIndex === index,
      label: `Obra ${item.id}`,
      testId: `home-obra-dot-${item.id}`,
    })),
    irParaObra: (id) =>
      setIndex(
        Math.max(
          0,
          obras.findIndex((item) => item.id === id),
        ),
      ),
    obraAnterior: () => irPara(index - 1),
    proximaObra: () => irPara(index + 1),
  };
}
